import {
  createFileRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import { Trans, useTranslation } from "react-i18next";
import { useAppForm } from "@/components/app-form.tsx";
import { revalidateLogic } from "@tanstack/react-form";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal.tsx";
import { getServerUrl } from "@/lib/runtime-config";
import { z } from "zod";
import { Button } from "@/components/ui/button.tsx";
import { useShop } from "@/hooks/use-shop.ts";
import { useApiErrorHandler } from "@/lib/api";
import { useLanguage } from "@/hooks/use-language";
import { useCountryCode } from "@frontend-common/country-provider";
import { AudioSlot, useAudioSlot } from "@/features/audio/kiosk-audio";

export const reminderSchema = z.object({
  email: z.string().email(),
});
declare module "@tanstack/react-router" {
  interface HistoryState {
    /** Email typed into the login form, prefilled here. */
    loginEmail?: string;
  }
}

export const Route = createFileRoute("/password-reminder")({
  component: RouteComponent,
});

function RouteComponent() {
  useAudioSlot("page.password-reminder");
  const { showModal, closeModal } = useMuiModal();
  const { t } = useTranslation();
  const { data: shop } = useShop();
  const navigate = useNavigate();
  const errorHandler = useApiErrorHandler();
  const language = useLanguage();
  const country = useCountryCode();
  const loginEmail = useRouterState({
    select: (state) => state.location.state.loginEmail,
  });

  const form = useAppForm({
    defaultValues: { email: loginEmail ?? "" },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: reminderSchema,
      onSubmitAsync: async ({ value }) => {
        const response = await fetch(
          `${getServerUrl()}/users/reminder`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Kiosk-Key": shop.kioskKey,
              "lang-key": language,
              "country-code": country,
            },
            body: JSON.stringify({
              email: value.email,
            }),
          },
        );

        const data = await errorHandler(response, undefined);

        if (data !== null) {
          showModal(
            <Modal>
              <AudioSlot slot="modal.password-reminder-sent" />
              <ModalTitle>
                <Trans>Figyelmeztetés!</Trans>
              </ModalTitle>
              <ModalContent>
                <p>
                  <Trans>
                    A jelszó emlékeztetőt elküldtük az általad megadott e-mail
                    címre!
                  </Trans>
                </p>
                <Button
                  onClick={() => {
                    closeModal();
                    navigate({ to: `/login-screen` });
                  }}
                >
                  <Trans>Ok</Trans>
                </Button>
              </ModalContent>
            </Modal>,
          );
          return;
        }
      },
    },
  });

  return (
    <Page>
      <Header backArrowHref={`/login-screen`} />
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-8`}>
          <Trans>Elfelejtett belépési adatok</Trans>
        </h2>
        <p className={`flex gap-x-1 items-center text-lg leading-6 mb-12`}>
          <img src={`/arrow-attention.svg`} className={`size-8`} />
          <Trans>
            Kérjük, add meg az email címedet, amit a Bubbles regisztrációnál
            megadtál, és amire a jelszóemlékeztetőt küldeni tudjuk.
          </Trans>
        </p>
        <form.AppForm>
          <form
            className="space-y-12"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.AppField
              name="email"
              children={(field) => (
                <field.TextField
                  tabIndex={1}
                  type="email"
                  label={t("E-mail*")}
                  disableHorizontalGrowth
                  className={`[&_input]:w-xl [&_input]:py-10 [&_input]:text-5xl [&_label]:text-center [&_label]:text-3xl grid justify-center gap-4`}
                />
              )}
            />

            <div className="mt-20 grid gap-10 items-center justify-center">
              <form.Subscribe
                selector={(state) =>
                  [state.values, state.isSubmitting] as const
                }
                children={([values, isSubmitting]) => (
                  <Button
                    className={`font-bold w-xl py-7 text-4xl px-0`}
                    type="submit"
                    disabled={
                      !reminderSchema.safeParse(values).success || isSubmitting
                    }
                  >
                    <Trans>Jelszóemlékeztető küldése</Trans>
                  </Button>
                )}
              />
            </div>
          </form>
        </form.AppForm>
        <Footer />
      </PageContent>
    </Page>
  );
}
